import React, { useEffect } from 'react';
import Layout from '../components/common/Layout';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import UploadQuestionsDialog from '../components/UploadQuestionsDialog';
import useOptionConfig from '../hooks/useOptionConfig';
import useUploadQuestionsStore from '../store/uploadQuestionsStore';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import Dropdown from '../components/common/Dropdown';
import QuestionBankItem from '../components/QuestionBankItem';
import useQuestionBankPageState from '../hooks/useQuestionBankPageState';
import PageHeader from '../components/common/PageHeader';
import { usePrivileges } from '../config/privilegeConstant';

const QuestionBankPage = () => {
  const {
    loading,
    error,
    courses,
    syllabuses,
    subjects,
    units,
    modules,
    topics,
    selectedCourse,
    selectedSyllabus,
    selectedSubject,
    selectedUnit,
    selectedModule,
    selectedTopic,
    searchTerm,
    searchId,
    page,
    rowsPerPage,
    month,
    year,
    availableMonths,
    availableYears,
    setPage,
    safeQuestions,
    paginatedQuestions,
    handleSearch,
    handleSearchId,
    handleMonthChange,
    handleYearChange,
    handleCourseChange,
    handleSyllabusChange,
    handleSubjectChange,
    handleUnitChange,
    handleModuleChange,
    handleTopicChange,
    reset
  } = useQuestionBankPageState();
  const { config } = useOptionConfig();
  const labels = config?.labels?.questionBank || {};
  const { isOpen, openDialog, closeDialog } = useUploadQuestionsStore();
  const { hasPrivilege, codes } = usePrivileges();

  const monthOptionsToRender = (availableMonths && availableMonths.length > 0)
    ? availableMonths.map((opt) => {
        if (typeof opt === 'object' && opt !== null) {
          return { value: opt.value ?? opt.month ?? opt.code ?? '', label: opt.label ?? opt.month ?? opt.name ?? '' };
        }
        return { value: String(opt), label: String(opt) };
      })
    : (labels.monthOptions || []);

  const yearOptionsToRender = (availableYears && availableYears.length > 0)
    ? availableYears.map((opt) => {
        if (typeof opt === 'object' && opt !== null) {
          return { value: String(opt.value ?? opt.year ?? ''), label: String(opt.label ?? opt.year ?? '') };
        }
        return { value: String(opt), label: String(opt) };
      })
    : Array.from({ length: new Date().getFullYear() + 2 - 2000 }, (_, i) => {
        const y = new Date().getFullYear() + 1 - i;
        return { value: String(y), label: String(y) };
      });

  return (
    <Layout>
      <PageHeader
        title={labels.pageTitle || "Question Bank"}
        subtitle={labels.pageSubtitle || "Search and manage all questions in the Question Bank."}
        rightAction={
          hasPrivilege(codes.UPLOAD_QB_QUESTIONS) && (
            <Button onClick={openDialog}>
              {labels.btnUpload || "Upload Question_Bank"}
            </Button>
          )
        }
      />
      <Paper sx={{ p: 4 }}>
        <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
          <Grid item xs={12} sm={4} md={2}>
            <Dropdown label={labels.lblCourse || "Course"} value={selectedCourse} onChange={(e) => handleCourseChange(e.target.value)} options={courses} />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <Dropdown label={labels.lblSyllabus || "Syllabus"} value={selectedSyllabus} onChange={(e) => handleSyllabusChange(e.target.value)} options={syllabuses} disabled={!selectedCourse} />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <Dropdown label={labels.lblSubject || "Subject"} value={selectedSubject} onChange={(e) => handleSubjectChange(e.target.value)} options={subjects} disabled={!selectedSyllabus} />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <Dropdown label={labels.lblUnit || "Unit"} value={selectedUnit} onChange={(e) => handleUnitChange(e.target.value)} options={units} disabled={!selectedSubject} />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <Dropdown label={labels.lblModule || "Module"} value={selectedModule} onChange={(e) => handleModuleChange(e.target.value)} options={modules} disabled={!selectedUnit} />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <Dropdown label={labels.lblTopic || "Topic"} value={selectedTopic} onChange={(e) => handleTopicChange(e.target.value)} options={topics} disabled={!selectedModule} />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
          <Grid item xs={12} sm={4} md={2}>
            <Input
              label={labels.searchIdPlaceholder || "Search by ID"}
              value={searchId}
              onChange={(e) => handleSearchId(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={8} md={4}>
            <Input
              label={labels.searchPlaceholder || "Search by question text"}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <Input
              select
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              label={labels.lblMonth || "Month"}
              value={month}
              onChange={(e) => handleMonthChange(e.target.value)}
            >
              <option value="">{labels.lblSelect || "Select"}</option>
              {monthOptionsToRender.map((opt, idx) => (
                <option key={idx} value={opt.value}>{opt.label}</option>
              ))}
            </Input>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <Input
              select
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              label={labels.lblYear || "Year"}
              value={year}
              onChange={(e) => handleYearChange(e.target.value)}
            >
              <option value="">{labels.lblSelect || "Select"}</option>
              {yearOptionsToRender.map((opt, idx) => (
                <option key={idx} value={opt.value}>{opt.label}</option>
              ))}
            </Input>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <Button variant="outlined" onClick={reset} fullWidth sx={{ mt: 1, height: '56px' }}>
              {labels.btnClearFilters || "Clear Filters"}
            </Button>
          </Grid>
        </Grid>

        {loading && <CircularProgress />}
        {error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && (
          <>
            {paginatedQuestions.map((question) => (
              <QuestionBankItem key={question.id ?? question.uuid ?? Math.random()} question={question} />
            ))}
            {paginatedQuestions.length === 0 && (
              <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                {labels.noQuestionsFound || "No questions found."}
              </Typography>
            )}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={Math.max(1, Math.ceil(safeQuestions.length / rowsPerPage))}
                page={page}
                onChange={(event, value) => {
                  setPage(value);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                color="primary"
              />
            </Box>
          </>
        )}
        <UploadQuestionsDialog open={isOpen} onClose={closeDialog} />
      </Paper>
    </Layout>
  );
};

export default QuestionBankPage;
